<template>
  <div class="measures-table">
    <div class="measures-table-content">
      <hot-table
        :class="className"
        :settings="settings"
        :style="style"
        ref="tableComponent"
      />
    </div>
  </div>
</template>

<script>
import Handsontable from "handsontable";
import { HotTable } from "@handsontable/vue";

const isChanged = (self, row, prop) => {
  const { trafficCount } = self.$store.state;
  const { rows } = trafficCount.fill;
  if (!self.myInstance) return false;
  const currentRowId = self.myInstance.getDataAtRowProp(row, "row_id");
  const initialObject = rows.find(o => o.row_id === currentRowId);
  const currentObject = self.myInstance.getSourceDataAtRow(row);

  if (!currentRowId) return false;
  if (!initialObject) return false;
  if (initialObject[prop] !== currentObject[prop]) return true;

  return false;
};

const customRenderer = self => {
  return function(instance, td, row, col, prop, value, cellProp) {
    if (prop === "turn_name") {
      Handsontable.renderers.TextRenderer.apply(this, arguments);
      return;
    } else {
      Handsontable.renderers.NumericRenderer.apply(this, arguments);
    }

    if (
      row === self.$store.state.trafficCount.fill.rows.length ||
      col === 0 ||
      col === self.$store.state.trafficCount.fill.columns.length
    ) {
      cellProp.readOnly = true;
      td.classList.add("sum");
      return;
    }

    if (cellProp.valid) {
      if (isChanged(self, row, prop)) td.classList.add("changed");
    }
  };
};

const getColumns = that =>
  [...that.$store.state.trafficCount.fill.columns, "Итого"].map(data => {
    switch (data) {
      default:
        return {
          type: data === "name" || data === "turn_name" ? "text" : "numeric",
          data,
          renderer: customRenderer(that)
        };
    }
  });

const getColHeaders = that => {
  const { detalization_type, columns } = that.$store.state.trafficCount.fill;

  if (detalization_type === 1) {
    return ["Направление", ...columns.slice(1), "Итого"];
  } else {
    return ["Направление", "Наименование", ...columns.slice(2), "Итого"];
  }
};

const columnsToRemove = ["name", "turn_name", "row_id", "Итого"];

export default {
  components: {
    HotTable
  },
  data() {
    return {
      className: "r-table",
      myInstance: null,
      settings: {
        data: this.$store.state.trafficCount.fill.rows,
        colHeaders: getColHeaders(this),
        columns: getColumns(this),
        stretchH: "all",
        readOnly: this.$store.state.trafficCount.fill.coefsEnabled,
        afterSelection: this.afterSelection,
        afterDeselect: this.afterDeselect,
        afterChange: this.afterChange
      },
      style: "width: auto; height: 100%; overflow: hidden;",
      saving: false
    };
  },
  watch: {
    "$store.state.trafficCount.fill.rows": {
      handler: function() {
        this.setTableData();
      },
      deep: true
    }
  },
  methods: {
    rerenderTable() {
      this.$nextTick(() => this.myInstance.render());
    },
    setTableData() {
      const {
        rows,
        columns,
        coefsEnabled
      } = this.$store.state.trafficCount.fill;
      const categories = columns.filter(f => f !== "name" && f !== "turn_name");
      const data = [
        ...rows.map(obj => ({
          ...obj,
          Итого: this.getRowSum(obj)
        })),
        {
          name: "Итого",
          ...categories.reduce((prev, category) => {
            prev[category] = this.getCellSum(rows, category);
            return prev;
          }, {})
        }
      ];
      if (this.myInstance) this.myInstance.loadData(data);
      this.settings.readOnly = coefsEnabled;
      this.rerenderTable();
    },
    getRowSum(obj) {
      const { columns } = this.$store.state.trafficCount.fill;

      return Object.keys(obj)
        .filter(f => !columnsToRemove.includes(f) && columns.indexOf(f) > -1)
        .reduce((prev, el) => prev + Number(obj[el] || 0), 0);
    },
    getCellSum(tableData, category) {
      return tableData.reduce(
        (prev, el) => prev + Number(el[category] || 0),
        0
      );
    },
    cancelEditing() {
      this.setTableData();
      this.$emit("changed", false);
    },
    parseTableData() {
      const {
        activeCp,
        interval,
        date,
        transport_systems,
        detalization_type
      } = this.$store.state.trafficCount.fill;
      const tableData = this.myInstance.getSourceData();
      const filtered = tableData.filter(t => t.row_id);
      const data = [];
      const turn_names = [];

      for (const row of filtered) {
        const categories = Object.keys(row).filter(
          key => !columnsToRemove.includes(key)
        );
        categories.forEach(code => {
          const transport = transport_systems.find(t => t.code === code);
          const measure = {
            count_point_id: activeCp,
            measure_interval_id: interval,
            transport_id: transport.id,
            measure_date: date,
            value: row[code]
          };

          if (detalization_type === 2) measure.turn_id = row.row_id;
          else measure.rev = row.row_id !== "direct";

          data.push(measure);
        });

        if (detalization_type === 2) {
          turn_names.push({
            id: row.row_id,
            name: row.turn_name
          });
        }
      }

      return {
        data,
        turn_names
      };
    },
    async saveMeasures() {
      try {
        this.$emit("saving", true);
        const { ids } = this.$store.state.trafficCount.model;
        const measuresId = ids["measures"];
        const measuresUrl = `objectInfo/${measuresId}`;
        const turnsId = ids["turns"];
        const turnsUrl = `objectInfo/${turnsId}`;
        const { data, turn_names } = this.parseTableData();

        await this.$store.dispatch("POST_REQUEST", { url: measuresUrl, data });
        await this.$store.dispatch("POST_REQUEST", {
          url: turnsUrl,
          data: turn_names
        });

        this.$emit("saving", false);
        this.$emit("changed", false);
        this.$emit("update");
      } catch (error) {
        console.log(error);
        this.$emit("saving", false);
      }
    },
    afterChange(changes) {
      const { columns, rows } = this.$store.state.trafficCount.fill;

      if (changes) {
        changes.forEach(([row, prop]) => {
          if (prop === "Итого") return;
          if (prop === "turn_name") return;
          if (row === rows.length) return;

          const col = columns.indexOf(prop);
          const rowData = this.myInstance.getSourceDataAtRow(row);
          const colData = this.myInstance.getSourceDataAtCol(col);
          const rowSum = this.getRowSum(rowData);
          const colSum = colData
            .slice(0, -1)
            .reduce((prev, el) => prev + Number(el || 0), 0);
          this.myInstance.setDataAtCell(row, columns.length, rowSum);
          this.myInstance.setDataAtCell(rows.length, col, colSum);
        });

        this.$emit("changed", true);
      }
    },
    afterSelection(row1, col1, row2) {
      const rowData = this.$refs.tableComponent.hotInstance.getSourceDataAtRow(
        row2
      );
      const { row_id } = rowData;
      this.$store.commit("SET_TC_FILL_PROP", {
        name: "hoveredTurn",
        value: row_id || null
      });
    },
    afterDeselect() {
      this.$store.commit("SET_TC_FILL_PROP", {
        name: "hoveredTurn",
        value: null
      });
    }
  },
  mounted() {
    this.myInstance = this.$refs.tableComponent.hotInstance;
  }
};
</script>

<style></style>
